// Monetisation → Web payments (contracts/web.md §5.7): Stripe keys per mode, the payments.stripe settings,
// packages, transactions and webhook events. Editors read (payments.view), admins change (payments.configure);
// viewers get 403. Mutation responses aren't read: the panel refetches what changed.

import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import type { PaymentMode } from "../lib/payments";
import { request } from "./client";
import { keys } from "./queries";
import {
  Packages,
  StripeSettings,
  Transactions,
  WebhookEvents,
  type PackageInput,
  type PackagePatch,
  type StripeSettingsPatch,
} from "./paymentsSchemas";

const enc = encodeURIComponent;
export const PAGE_SIZE = 25;

export const paymentsApi = {
  stripe: (signal?: AbortSignal) => request(StripeSettings, "GET", "/payments/stripe", { signal }),
  /** Validates the key with Stripe, creates the webhook endpoint and stores both keys (§5.2). Returns the connection. */
  connect: (mode: PaymentMode, secretKey: string, publishableKey: string) =>
    request(z.unknown(), "PUT", `/payments/stripe/keys/${mode}`, { body: { secretKey, publishableKey } }),
  /** Deletes the webhook endpoint (best effort) and the secrets. 204. */
  disconnect: (mode: PaymentMode) => request(z.unknown(), "DELETE", `/payments/stripe/keys/${mode}`),
  updateSettings: (patch: StripeSettingsPatch) => request(z.unknown(), "PATCH", "/payments/stripe", { body: patch }),

  packages: (mode: PaymentMode, signal?: AbortSignal) =>
    request(Packages, "GET", "/payments/packages", { query: { mode }, signal }).then((r) => ({
      packages: r.packages.map((p) => ({ ...p, mode: p.mode ?? mode })),
    })),
  createPackage: (input: PackageInput) => request(z.unknown(), "POST", "/payments/packages", { body: input }),
  createDefaults: (mode: PaymentMode) => request(z.unknown(), "POST", "/payments/packages/defaults", { body: { mode } }),
  updatePackage: (id: string, patch: PackagePatch) => request(z.unknown(), "PATCH", `/payments/packages/${enc(id)}`, { body: patch }),
  archivePackage: (id: string) => request(z.unknown(), "POST", `/payments/packages/${enc(id)}/archive`, { body: {} }),

  transactions: (mode: PaymentMode, cursor?: string | null, signal?: AbortSignal) =>
    request(Transactions, "GET", "/payments/transactions", { query: { mode, cursor, limit: PAGE_SIZE }, signal }),
  /** The newest webhook events; `cursor` is only sent back when the server returned a `nextCursor`. */
  events: (mode: PaymentMode, cursor?: string | null, signal?: AbortSignal) =>
    request(WebhookEvents, "GET", "/payments/events", { query: { mode, cursor, limit: PAGE_SIZE }, signal }),
};

export const paymentsKeys = {
  all: ["payments"] as const,
  stripe: ["payments", "stripe"] as const,
  packagesAll: ["payments", "packages"] as const,
  packages: (mode: PaymentMode) => ["payments", "packages", mode] as const,
  transactions: (mode: PaymentMode) => ["payments", "transactions", mode] as const,
  events: (mode: PaymentMode) => ["payments", "events", mode] as const,
};

export const useStripeSettings = (enabled = true) =>
  useQuery({ queryKey: paymentsKeys.stripe, queryFn: ({ signal }) => paymentsApi.stripe(signal), enabled });

export const usePackages = (mode: PaymentMode, enabled = true) =>
  useQuery({ queryKey: paymentsKeys.packages(mode), queryFn: ({ signal }) => paymentsApi.packages(mode, signal), enabled });

export const useTransactions = (mode: PaymentMode, enabled = true) =>
  useInfiniteQuery({
    queryKey: paymentsKeys.transactions(mode),
    queryFn: ({ pageParam, signal }) => paymentsApi.transactions(mode, pageParam, signal),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    enabled,
  });

export const useWebhookEvents = (mode: PaymentMode, enabled = true) =>
  useInfiniteQuery({
    queryKey: paymentsKeys.events(mode),
    queryFn: ({ pageParam, signal }) => paymentsApi.events(mode, pageParam, signal),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    enabled,
  });

/** A mutation that refetches the given keys (and the audit log) once it succeeds. */
function usePaymentsMutation<V>(fn: (v: V) => Promise<unknown>, invalidate: (v: V) => readonly (readonly unknown[])[]) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: (_data, v) => {
      for (const queryKey of invalidate(v)) void client.invalidateQueries({ queryKey });
      void client.invalidateQueries({ queryKey: keys.audit });
    },
  });
}

/** Connecting or disconnecting changes the connection cards, and the webhook log of that mode. */
export const useConnectStripe = () =>
  usePaymentsMutation(
    ({ mode, secretKey, publishableKey }: { mode: PaymentMode; secretKey: string; publishableKey: string }) => paymentsApi.connect(mode, secretKey, publishableKey),
    ({ mode }) => [paymentsKeys.stripe, paymentsKeys.events(mode)],
  );
export const useDisconnectStripe = () =>
  usePaymentsMutation(
    (mode: PaymentMode) => paymentsApi.disconnect(mode),
    (mode) => [paymentsKeys.stripe, paymentsKeys.events(mode)],
  );
export const useUpdateStripeSettings = () =>
  usePaymentsMutation(
    (patch: StripeSettingsPatch) => paymentsApi.updateSettings(patch),
    () => [paymentsKeys.stripe],
  );

export const useCreatePackage = () =>
  usePaymentsMutation(
    (input: PackageInput) => paymentsApi.createPackage(input),
    ({ mode }) => [paymentsKeys.packages(mode)],
  );
export const useCreateDefaultPackages = () =>
  usePaymentsMutation(
    (mode: PaymentMode) => paymentsApi.createDefaults(mode),
    (mode) => [paymentsKeys.packages(mode)],
  );
export const useUpdatePackage = () =>
  usePaymentsMutation(
    ({ id, patch }: { id: string; mode: PaymentMode; patch: PackagePatch }) => paymentsApi.updatePackage(id, patch),
    ({ mode }) => [paymentsKeys.packages(mode)],
  );
export const useArchivePackage = () =>
  usePaymentsMutation(
    ({ id }: { id: string; mode: PaymentMode }) => paymentsApi.archivePackage(id),
    ({ mode }) => [paymentsKeys.packages(mode)],
  );
